'use client';

import { useEffect } from 'react';
import type { FieldValues, UseFormClearErrors, UseFormSetValue } from 'react-hook-form';

import { RichTextEditor } from '@mantine/tiptap';
import {
  IconBold,
  IconClearFormatting,
  IconItalic,
  IconList,
  IconListNumbers,
  IconUnderline,
} from '@tabler/icons-react';
import type { Extension } from '@tiptap/core';
import { Placeholder } from '@tiptap/extension-placeholder';
import { Underline } from '@tiptap/extension-underline';
import { useEditor } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';

interface IWysiwygEditor {
  id: string;
  content?: string;
  placeholder: string;
  forceRefresh?: boolean;
  setValue: UseFormSetValue<FieldValues>;
  setValueKey: string;
  clearErrors?: UseFormClearErrors<FieldValues>;
  readOnly?: boolean;
  error?: string;
}

const BoldIcon = () => <IconBold size={18} stroke={1.5} />;
const ItalicIcon = () => <IconItalic size={18} stroke={1.5} />;
const UnderlineIcon = () => <IconUnderline size={18} stroke={1.5} />;
const ClearFormattingIcon = () => <IconClearFormatting size={18} stroke={1.5} />;
const ListIcon = () => <IconList size={18} stroke={1.5} />;
const ListNumbersIcon = () => <IconListNumbers size={18} stroke={1.5} />;

export const WysiwygEditor = ({
  id,
  content,
  placeholder,
  forceRefresh,
  setValue,
  setValueKey,
  clearErrors,
  readOnly,
  error,
}: IWysiwygEditor) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({}) as Extension,
      Underline,
      Placeholder.configure({
        placeholder: placeholder,
      }),
    ],
    editorProps: {
      attributes: {
        class: 'min-h-[150px]',
      },
      transformPastedHTML(html) {
        const newHtml = html.replace(/(<(?!\/?(strong|i|b|u|ul|ol|li|p|br)\b)[^>]*>)/gi, ' ');
        return newHtml;
      },
    },
    content: content,
    editable: readOnly ? !readOnly : true,
    onUpdate() {
      clearErrors && clearErrors('customModelAgreement');
    },
    onBlur({ editor }) {
      clearErrors && clearErrors('customModelAgreement');
      setValue &&
        setValue(setValueKey, editor.getHTML().replaceAll(/<li><p>(.*?)<\/p><(\/?)(ol|li|ul)>/gi, '<li>$1<$2$3>'));
    },

    injectCSS: false,
  });

  useEffect(() => {
    if (!editor) return;
    if (content === null) editor.commands.setContent('');
  }, [content, editor]);

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(readOnly ? !readOnly : true);
  }, [readOnly, editor]);

  // Force refresh gets changed based on templateSelection has changed, false or true doesn't matter as long as the useEffect gets called
  useEffect(() => {
    content && editor && editor.commands && editor.commands?.setContent(content);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceRefresh]);

  if (typeof window === 'undefined' || typeof document === 'undefined') return <></>;

  return (
    <>
      <RichTextEditor
        data-testid={id}
        id={id}
        editor={editor}
        className={error ? '!border-1 !border !border-solid !border-red-600' : ''}
      >
        <RichTextEditor.Toolbar sticky stickyOffset={-48}>
          <RichTextEditor.ControlsGroup>
            <RichTextEditor.Bold icon={BoldIcon} />
            <RichTextEditor.Italic icon={ItalicIcon} />
            <RichTextEditor.Underline icon={UnderlineIcon} />
            <RichTextEditor.ClearFormatting icon={ClearFormattingIcon} />
          </RichTextEditor.ControlsGroup>

          <RichTextEditor.ControlsGroup>
            <RichTextEditor.BulletList icon={ListIcon} />
            <RichTextEditor.OrderedList icon={ListNumbersIcon} />
          </RichTextEditor.ControlsGroup>
        </RichTextEditor.Toolbar>
        <RichTextEditor.Content />
      </RichTextEditor>
      {error && <span className="font-base text-xs text-red-600">{error}</span>}
    </>
  );
};

WysiwygEditor.displayName = 'WysiwygEditor';
